import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Users, FileText, Receipt, FolderKanban, UserPlus, Package, CheckSquare, Loader2 } from 'lucide-react'
import { searchApi } from '@/lib/api'
import type { SearchResult, SearchEntityType, GroupedSearchResponse } from '@/types'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'

// Entity type icons and labels
const entityConfig: Record<SearchEntityType, { icon: React.ElementType; label: string; color: string }> = {
  CLIENT: { icon: Users, label: 'Clients', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300' },
  INVOICE: { icon: FileText, label: 'Invoices', color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300' },
  QUOTE: { icon: Receipt, label: 'Quotes', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300' },
  EXPENSE: { icon: Receipt, label: 'Expenses', color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300' },
  PROJECT: { icon: FolderKanban, label: 'Projects', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300' },
  LEAD: { icon: UserPlus, label: 'Leads', color: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-300' },
  PRODUCT: { icon: Package, label: 'Products', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300' },
  TASK: { icon: CheckSquare, label: 'Tasks', color: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300' },
}

interface GlobalSearchProps {
  textColor?: string
}

export function GlobalSearch({ textColor }: GlobalSearchProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<GroupedSearchResponse | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const selectedItemRef = useRef<HTMLButtonElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Debounced search
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.length < 2) {
      setResults(null)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await searchApi.searchGrouped(searchQuery, 5)
      setResults(response)
      setSelectedIndex(-1)
    } catch (err) {
      console.error('Search failed:', err)
      setError(t('search.error', 'Search failed. Please try again.'))
      setResults(null)
    } finally {
      setIsLoading(false)
    }
  }, [t])

  // Handle input change with debounce
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query)
    }, 300)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [query, performSearch])

  // Get all results as a flat array for keyboard navigation
  const allResults = results ? (Object.keys(results.groups) as SearchEntityType[])
    .filter(type => results.groups[type] && results.groups[type].length > 0)
    .flatMap(type => results.groups[type]) : []

  // Handle result click
  const handleResultClick = (result: SearchResult) => {
    setOpen(false)
    setQuery('')
    setResults(null)
    setSelectedIndex(-1)
    navigate(result.url)
  }

  // Scroll selected item into view
  useEffect(() => {
    if (selectedItemRef.current && scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const item = selectedItemRef.current

      const containerRect = container.getBoundingClientRect()
      const itemRect = item.getBoundingClientRect()

      // Check if item is above visible area
      if (itemRect.top < containerRect.top) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
      // Check if item is below visible area
      else if (itemRect.bottom > containerRect.bottom) {
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      }
    }
  }, [selectedIndex])

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex(prev => Math.min(prev + 1, allResults.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex(prev => Math.max(prev - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (selectedIndex >= 0 && selectedIndex < allResults.length) {
        handleResultClick(allResults[selectedIndex])
      } else if (allResults.length > 0) {
        // If no selection, navigate to first result
        handleResultClick(allResults[0])
      }
    } else if (e.key === 'Escape') {
      setOpen(false)
      setQuery('')
      setResults(null)
      inputRef.current?.blur()
    }
  }

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen(true)
        setTimeout(() => inputRef.current?.focus(), 100)
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown)
    return () => document.removeEventListener('keydown', handleGlobalKeyDown)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Get all entity types that have results
  const entityTypes = results ? (Object.keys(results.groups) as SearchEntityType[]).filter(
    type => results.groups[type] && results.groups[type].length > 0
  ) : []

  // Calculate result index for highlighting
  const getResultIndex = (entityType: SearchEntityType, itemIndex: number): number => {
    let index = 0
    for (const type of entityTypes) {
      if (type === entityType) {
        return index + itemIndex
      }
      index += results?.groups[type]?.length || 0
    }
    return -1
  }

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setOpen(true)}
          placeholder={t('search.placeholder', 'Search everything...')}
          className="w-full h-9 pl-10 pr-16 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-neutral-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-muted-foreground"
          style={{ color: textColor }}
        />
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
          <span className="text-xs">⌘</span>K
        </kbd>
      </div>

      {/* Dropdown Results */}
      {open && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg z-50 overflow-hidden">
        <div ref={scrollContainerRef} className="max-h-[400px] overflow-y-auto">
          {/* Loading state */}
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Error state */}
          {error && !isLoading && (
            <div className="flex items-center justify-center py-8 text-sm text-destructive">
              {error}
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && query.length >= 2 && entityTypes.length === 0 && (
            <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
              <Search className="h-8 w-8 mb-2 opacity-50" />
              <p>{t('search.noResults', 'No results found')}</p>
              <p className="text-xs mt-1">{t('search.tryDifferent', 'Try a different search term')}</p>
            </div>
          )}

          {/* Initial state */}
          {!isLoading && !error && query.length < 2 && (
            <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
              <Search className="h-8 w-8 mb-2 opacity-50" />
              <p>{t('search.startTyping', 'Start typing to search...')}</p>
            </div>
          )}

          {/* Results */}
          {!isLoading && !error && entityTypes.length > 0 && results && (
            <div className="py-2">
              {entityTypes.map((entityType) => {
                const config = entityConfig[entityType]
                const Icon = config.icon
                const items = results.groups[entityType]

                return (
                  <div key={entityType} className="mb-2">
                    <div className="px-3 py-1.5 text-xs font-medium text-muted-foreground flex items-center gap-2">
                      <Icon className="h-3.5 w-3.5" />
                      {config.label}
                      <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0">
                        {items.length}
                      </Badge>
                    </div>
                    {items.map((result, itemIndex) => {
                      const resultIndex = getResultIndex(entityType, itemIndex)
                      const isSelected = resultIndex === selectedIndex

                      return (
                        <button
                          key={result.id}
                          ref={isSelected ? selectedItemRef : null}
                          onClick={() => handleResultClick(result)}
                          className={cn(
                            "w-full px-3 py-2 text-left hover:bg-accent transition-colors flex items-start gap-3",
                            isSelected && "bg-accent"
                          )}
                        >
                          <div className={cn('p-1.5 rounded-md mt-0.5', config.color)}>
                            <Icon className="h-3.5 w-3.5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-sm truncate">{result.title}</div>
                            {result.subtitle && (
                              <div className="text-xs text-muted-foreground truncate">{result.subtitle}</div>
                            )}
                            <div className="flex items-center gap-2 mt-0.5">
                              {result.status && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  {result.status}
                                </Badge>
                              )}
                              {result.amount !== undefined && result.currency && (
                                <span className="text-xs text-muted-foreground">
                                  {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: result.currency,
                                  }).format(result.amount)}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      )
                    })}
                  </div>
                )
              })}

              {/* Total count and keyboard hint */}
              <div className="px-3 py-2 text-xs text-muted-foreground border-t flex items-center justify-between">
                <span>{t('search.totalResults', 'Found {{count}} results', { count: results.total })}</span>
                <span className="text-[10px]">↑↓ to navigate, Enter to select</span>
              </div>
            </div>
          )}
        </div>
        </div>
      )}
    </div>
  )
}

